import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import configDetails from '../Config/Config';
import routesfe from '../Config/RoutesFE';
import ERROR_CODES from '../Config/ErrorCodes';
import { Dropdown } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import DOMPurify from "dompurify";
import { BsPersonFill } from 'react-icons/bs';
import logger from '../utils/Logger';

import '../Styles/LiveTranscript.css';

const COMPONENT_NAME = 'LiveTranscript';



// Custom hook for responsive design
const useResponsive = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth > 768 && window.innerWidth <= 1024);

  useEffect(() => {
    // Ensure viewport meta tag is set for mobile
    const viewport = document.querySelector('meta[name="viewport"]');
    if (!viewport) {
      const meta = document.createElement('meta');
      meta.name = 'viewport';
      meta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      setIsTablet(width > 768 && width <= 1024);
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Call once to set initial state

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return { isMobile, isTablet };
};
// Helper to generate a random UUID (RFC4122 version 4 compliant)
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

const LiveTranscript = () => {
  const [status, setStatus] = useState('Connecting…');
  // Viewer preferences
  const [viewerTextSize, setViewerTextSize] = useState('medium'); // small, medium, large, xlarge
  const [viewerFont, setViewerFont] = useState('system'); // system, sans, serif, mono
  const [showPrefDropdown, setShowPrefDropdown] = useState(false);

  // Load preferences from localStorage on mount
  useEffect(() => {
    try {
      const prefs = JSON.parse(localStorage.getItem('viewerTranscriptPrefs'));
      if (prefs && typeof prefs === 'object') {
        if (['small','medium','large','xlarge'].includes(prefs.textSize)) {
          setViewerTextSize(prefs.textSize);
        }
        if (['system','sans','serif','mono'].includes(prefs.font)) {
          setViewerFont(prefs.font);
        }
      }
    } catch {}
  }, []);

  // Save preferences to localStorage when changed
  useEffect(() => {
    localStorage.setItem('viewerTranscriptPrefs', JSON.stringify({ textSize: viewerTextSize, font: viewerFont }));
  }, [viewerTextSize, viewerFont]);

  const [statusFetched, setStatusFetched] = useState(false);
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true); // Default: disabled
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [disableAudioByProfile, setDisableAudioByProfile] = useState(false);
  const showLiveAudioOption = Boolean(configDetails.sendAudioChunk !== false && configDetails.sendAudioChunk !== 'false');
  // Store transcript segments as a map: { div_id: [{text, final}] }
  const [segmentMap, setSegmentMap] = useState({});
  const [divOrder, setDivOrder] = useState([]); // To preserve paragraph order
  const autoScrollRef = useRef(autoScrollEnabled);
  const outRef = useRef(null);
  const lastPartialIndexRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioEnabledRef = useRef(false); // Add ref to track audio state for WebSocket closure
  const nextStartTimeRef = useRef(0); // to queue audio chunks smoothly
  const navigate = useNavigate();
  const location = useLocation();
  const accessParam = new URLSearchParams(location.search).get('access');
  const isPublicAccess = accessParam === 'public';
  const { isMobile, isTablet } = useResponsive();
  const latestSegmentRef = useRef(null);
  const bottomRef = useRef(null); 
  const s3DivIdsRef = useRef(new Set());
  const paragraphArrivalTimesRef = useRef({});
  const firstParagraphArrivalTimeRef = useRef(null);
  const lastHistoricalStartTimeRef = useRef(null);
  const lastHistoricalArrivalRef = useRef(null);

  // --- Public access validation (polling after recording stops) ---
  const wsClosedRef = useRef(false);            // true once the viewer WebSocket closes
  const statusStoppedRef = useRef(false);       // true once recording status is 'Stopped'
  const accessRevokedRef = useRef(false);       // true once public access is confirmed revoked
  const validationPollingRef = useRef(null);    // interval id for the validate polling

  // Function to toggle audio enable/disable (immediate response)
  const toggleAudio = async () => {
    if (disableAudioByProfile) {
      logger.debug('Audio toggle blocked by profile settings', {
        component: COMPONENT_NAME,
        action: 'TOGGLE_AUDIO_BLOCKED',
        context: { reason: 'disabled_by_profile' }
      });
      return;
    }
    
    logger.info('Audio toggle clicked', {
      component: COMPONENT_NAME,
      action: 'CLICK_TOGGLE_AUDIO',
      context: {
        currentState: audioEnabled ? 'enabled' : 'disabled',
        newState: audioEnabled ? 'disabled' : 'enabled',
        timestamp: Date.now()
      }
    });
    
    try {
      if (!audioEnabled) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
        if (audioContextRef.current.state === 'suspended') {
          await audioContextRef.current.resume();
        }
        nextStartTimeRef.current = audioContextRef.current.currentTime;
        setAudioEnabled(true);
        audioEnabledRef.current = true;
        
        logger.info('Audio enabled successfully', {
          component: COMPONENT_NAME,
          action: 'AUDIO_ENABLED',
          context: { timestamp: Date.now() }
        });
      } else {
        if (audioContextRef.current) {
          audioContextRef.current.close();
          audioContextRef.current = null;
        }
        setAudioEnabled(false);
        audioEnabledRef.current = false;
        
        logger.info('Audio disabled', {
          component: COMPONENT_NAME,
          action: 'AUDIO_DISABLED',
          context: { timestamp: Date.now() }
        });
      }
    } catch (error) {
      logger.error('Failed to toggle audio', {
        component: COMPONENT_NAME,
        action: 'TOGGLE_AUDIO_ERROR',
        error,
        context: { audioEnabled, timestamp: Date.now() }
      });
    }
  };

  // Function to toggle auto-scroll (immediate response)
  const toggleAutoScroll = () => {
    logger.info('Auto-scroll toggle clicked', {
      component: COMPONENT_NAME,
      action: 'CLICK_TOGGLE_AUTOSCROLL',
      context: {
        currentState: autoScrollEnabled ? 'enabled' : 'disabled',
        newState: autoScrollEnabled ? 'disabled' : 'enabled',
        timestamp: Date.now()
      }
    });
    setAutoScrollEnabled(prev => !prev);
  };

  // Keep autoScrollRef in sync with autoScrollEnabled
  useEffect(() => {
    autoScrollRef.current = autoScrollEnabled;
    audioEnabledRef.current = audioEnabled;
  }, [autoScrollEnabled, audioEnabled]);

  const wrapWordsWithSpans = (inputText) => {
    if (!inputText || !inputText.trim()) return '';
    // Escape HTML
    const escapedText = inputText.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    // Split by spaces, preserve punctuation
    return escapedText.split(' ').map(word => {
      if (!word.trim()) return ' ';
      return `<span class="transcript-word">${word}</span>`;
    }).join(' ');
  };

  // Helper: group and accumulate text by div_id, accumulate all finals and latest partial after last final
  const getParagraphs = () => {
    const paragraphs = [];
    divOrder.forEach(div_id => {
      const segs = segmentMap[div_id] || [];
      let accumulatedText = '';
      let lastFinalIdx = -1;
      let hasFinalSegments = false;
      
      // Accumulate all finals
      segs.forEach((seg, idx) => {
        if (seg.final && seg.text && seg.text.trim()) {
          accumulatedText += (accumulatedText ? ' ' : '') + seg.text.trim();
          lastFinalIdx = idx;
          hasFinalSegments = true;
        }
      });
      
      // If there is a partial after the last final, append it
      if (lastFinalIdx !== -1) {
        for (let i = lastFinalIdx + 1; i < segs.length; i++) {
          if (!segs[i].final && segs[i].text && segs[i].text.trim()) {
            accumulatedText += ' ' + segs[i].text.trim();
          }
        }
      } else {
        // If no finals, accumulate all partials
        segs.forEach(seg => {
          if (!seg.final && seg.text && seg.text.trim()) {
            accumulatedText += (accumulatedText ? ' ' : '') + seg.text.trim();
          }
        });
      }
      
      if (accumulatedText && accumulatedText.trim()) {
        const trimmedText = accumulatedText.trim();
        
        // Filter out very short standalone fragments that are likely incomplete div_ids
        // But allow them if they're part of ongoing transcription (have final segments)
        const isStandaloneShortFragment = !hasFinalSegments && 
                                        trimmedText.length <= 8 && 
                                        trimmedText.split(/\s+/).length <= 2 &&
                                        !/[.!?]/.test(trimmedText);
        
        // Only exclude very short standalone fragments like "here is", "this is"
        if (!isStandaloneShortFragment) {
          const firstSeg = segs[0];
          let startTime = firstSeg?.start_time ?? null;
          if (startTime == null && paragraphArrivalTimesRef.current[div_id]) {
            if (lastHistoricalStartTimeRef.current != null && lastHistoricalArrivalRef.current != null) {
              startTime = Math.max(0, lastHistoricalStartTimeRef.current + Math.floor((paragraphArrivalTimesRef.current[div_id] - lastHistoricalArrivalRef.current) / 1000));
            } else if (firstParagraphArrivalTimeRef.current) {
              startTime = Math.max(0, Math.floor((paragraphArrivalTimesRef.current[div_id] - firstParagraphArrivalTimeRef.current) / 1000));
            }
          }
          paragraphs.push({ div_id, text: accumulatedText, start_time: startTime });
        }
      }
    });
    return paragraphs;
  };

  const handleConversationDeleted = (message, socketRef) => {
    logger.info('Conversation deleted, navigating', {
      component: COMPONENT_NAME,
      action: 'CONVERSATION_DELETED',
      context: {
        message,
        timestamp: Date.now()
      }
    });

    // Clear session storage
    sessionStorage.removeItem('viewerSessionId');
    sessionStorage.removeItem('conversationViewerId');

    // Prevent any further transcript rendering
    setSegmentMap({});
    setDivOrder([]);
    setStatus('Conversation Deleted');

    // Close WebSocket connection
    if (socketRef && socketRef.current) {
      try {
        socketRef.current.onmessage = null;
        socketRef.current.close();
      } catch (error) {
        logger.error('Error closing WebSocket', {
          component: COMPONENT_NAME,
          action: 'WEBSOCKET_CLOSE_ERROR',
          error
        });
      }
    }

    // Check if user is authenticated
    const userData = localStorage.getItem('user');
    const isAuthenticated = userData !== null;

    if (isAuthenticated) {
      // Navigate to home with state flag to show notification
      navigate(routesfe.HOME, { state: { conversationDeleted: true, deletedMessage: message } });
    } else {
      // Unauthorized user - navigate to landing page
      navigate(routesfe.LANDING, { state: { conversationDeleted: true, deletedMessage: message } });
    }
  };

  // Stop the public-access validation polling (if running)
  const stopAccessValidationPolling = () => {
    if (validationPollingRef.current) {
      clearInterval(validationPollingRef.current);
      validationPollingRef.current = null;
    }
  };

  // Handle a "revoked" response from the validate API:
  // clear all transcript data, close the socket and reuse the existing
  // "conversation unavailable" flow (same as a deleted conversation) so the
  // viewer never sees an empty conversation page.
  const handlePublicAccessRevoked = (message, socket) => {
    if (accessRevokedRef.current) return;
    accessRevokedRef.current = true;

    // Stop any further validation polling
    stopAccessValidationPolling();

    logger.info('Public access revoked, clearing transcript', {
      component: COMPONENT_NAME,
      action: 'PUBLIC_ACCESS_REVOKED',
      context: {
        message,
        timestamp: Date.now()
      }
    });

    // Clear all transcript-related data for this conversation
    setSegmentMap({});
    setDivOrder([]);
    lastPartialIndexRef.current = null;
    latestSegmentRef.current = null;
    s3DivIdsRef.current.clear();
    paragraphArrivalTimesRef.current = {};
    firstParagraphArrivalTimeRef.current = null;
    lastHistoricalStartTimeRef.current = null;
    lastHistoricalArrivalRef.current = null;

    setStatus('Access Revoked');
    setStatusFetched(true);

    // Close WebSocket connection (ignore any further messages)
    if (socket) {
      try {
        socket.onmessage = null;
        socket.close();
      } catch (error) {
        logger.error('Error closing WebSocket after access revocation', {
          component: COMPONENT_NAME,
          action: 'WEBSOCKET_CLOSE_ERROR',
          error
        });
      }
    }

    // Reuse the existing deleted/unavailable conversation navigation so the
    // viewer gets the standard "Conversation Unavailable" notification instead
    // of an empty page.
    const unavailableMessage =
      message || 'This conversation is unavailable or you no longer have access to view it.';
    const isAuthenticated = localStorage.getItem('user') !== null;
    if (isAuthenticated) {
      navigate(routesfe.HOME, { state: { conversationDeleted: true, deletedMessage: unavailableMessage } });
    } else {
      navigate(routesfe.LANDING, { state: { conversationDeleted: true, deletedMessage: unavailableMessage } });
    }
  };

  // Validate public access via GET /public_access/{conversationId}/validate
  const validatePublicAccess = async (conversationId, socket) => {
    try {
      const response = await fetch(
        `${configDetails.baseUrl}${configDetails.PUBLIC_ACCESS}/${conversationId}/validate`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        }
      );

      let data = null;
      try {
        data = await response.json();
      } catch (parseError) {
        data = null;
      }

      logger.info('Public access validation response', {
        component: COMPONENT_NAME,
        action: 'PUBLIC_ACCESS_VALIDATE_RESPONSE',
        context: {
          conversationId,
          httpStatus: response.status,
          status: data?.status,
          timestamp: Date.now()
        }
      });

      // Case 2 — Public access revoked: the backend explicitly reports
      // status: "revoked" (HTTP 403 with that payload). Other 403 responses
      // (restricted / non-revoked access errors) must NOT be treated as a
      // revocation — they keep the existing restricted-access behavior.
      if (data && (data.status === 'revoked' || data.public_access_revoked === true)) {
        handlePublicAccessRevoked(data?.message, socket);
        return;
      }

      // Case 1 — Public access allowed (HTTP 200 / status: "allowed"):
      // keep polling every 5 seconds so a later revocation is also caught.
    } catch (error) {
      logger.error('Error validating public access', {
        component: COMPONENT_NAME,
        action: 'PUBLIC_ACCESS_VALIDATE_ERROR',
        error,
        context: { conversationId, timestamp: Date.now() }
      });
    }
  };

  // Start polling the validate API every 5 seconds once the recording has
  // stopped (and the viewer's WebSocket connection has ended).
  const startAccessValidationPolling = (conversationId, socket) => {
    if (validationPollingRef.current || accessRevokedRef.current) return;

    logger.info('Starting public access validation polling', {
      component: COMPONENT_NAME,
      action: 'PUBLIC_ACCESS_VALIDATE_START',
      context: { conversationId, intervalMs: 5000, timestamp: Date.now() }
    });

    // Immediate check, then every 5 seconds
    validatePublicAccess(conversationId, socket);
    validationPollingRef.current = setInterval(() => {
      validatePublicAccess(conversationId, socket);
    }, 5000);
  };

  // Start validation polling only when the recording status is 'Stopped'
  // and the viewer is viewing via a public share link.
  const maybeStartAccessValidationPolling = (conversationId, socket) => {
    if (!isPublicAccess) return;
    if (statusStoppedRef.current && !accessRevokedRef.current) {
      startAccessValidationPolling(conversationId, socket);
    }
  };



  useEffect(() => {
    // Extract conversation ID from URL path (shared URL contains conversation_id)
    const pathParts = window.location.pathname.replace(/\/$/, '').split('/').filter(Boolean);
    const conversationId = pathParts[pathParts.length - 1];

    // Get user ID from localStorage (assuming similar pattern as Record.jsx)
    const getUserId = () => {
      const userData = localStorage.getItem('user');
      if (userData) {
        try {
          const user = JSON.parse(userData);
          return user.userSub || user.cognito_id || user.id || null;
        } catch (error) {
          logger.error('Error parsing user data', {
            component: COMPONENT_NAME,
            action: 'PARSE_USER_ERROR',
            error,
            context: { timestamp: Date.now() }
          });
          return null;
        }
      }
      return null;
    };

    const userId = getUserId();
    
    // Generate a new session UUID for this viewer session
    const sessionUUID = generateUUID();

    // Create a ref to store the socket for cleanup
    const socketRef = { current: null };

    logger.info('LiveTranscript component mounted', {
      component: COMPONENT_NAME,
      action: 'COMPONENT_MOUNT',
      userId,
      context: {
        conversationId,
        sessionUUID,
        isPublicAccess,
        pathname: window.location.pathname,
        browserOnline: navigator.onLine,
        timestamp: Date.now()
      },
      includeBrowserInfo: true
    });

    const tokenPayload = {
      user_id: userId,
      session_id: sessionUUID,
      conversation_id: conversationId,
      user_type: "viewer",
      connection_source: "web"
    };
    // Encode the entire object once
    const token = btoa(JSON.stringify(tokenPayload));
    // Construct WebSocket URL
    const ws_url = `${configDetails.websocketBaseUrl}?token=${token}`;
    
    logger.info('Connecting to WebSocket', {
      component: COMPONENT_NAME,
      action: 'WEBSOCKET_CONNECTING',
      userId,
      context: {
        conversationId,
        sessionUUID,
        timestamp: Date.now()
      }
    });
    
    const socket = new WebSocket(ws_url);
    socketRef.current = socket;

    // For public share links, validate access immediately on open so a
    // revoked conversation shows the "Conversation Unavailable" flow right
    // away instead of an empty conversation page.
    if (isPublicAccess) {
      validatePublicAccess(conversationId, socket);
    }

    // --- Ping interval for keeping connection alive ---
    let pingInterval = null;

    socket.onopen = () => {
      logger.info('WebSocket connected successfully', {
        component: COMPONENT_NAME,
        action: 'WEBSOCKET_CONNECTED',
        userId,
        context: {
          conversationId,
          timestamp: Date.now()
        }
      });
      
      const payload = {
        action: 'configuration_viewer',
        conversation_id: conversationId
      };
      socket.send(JSON.stringify(payload));

      // Start ping interval (every 5 seconds)
      pingInterval = setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ action: 'ping', session_id: sessionUUID, user_id: userId, conversation_id: conversationId, user_type: 'viewer' }));
        } else {
          clearInterval(pingInterval);
        }
      }, 5000);
    };

    socket.onmessage = (event) => {
      try {
        if (!event.data || typeof event.data !== 'string' || !event.data.trim().startsWith('{')) return;
        const data = JSON.parse(event.data);
        const outEl = outRef.current;
        if (!outEl) return;

        if (data.action === 'segment') {

          // Handle pause and stop state robustly
          const isPaused = data.is_pause === true;
          const isStopped = data.is_stop === true;
          const isCorrected = data.is_corrected === true;
          
          if (isStopped) {
            logger.info('Recording stopped', {
              component: COMPONENT_NAME,
              action: 'STATUS_STOPPED',
              context: { conversationId, timestamp: Date.now() }
            });
            setStatus('Stopped');
            statusStoppedRef.current = true;
            // If the WebSocket already closed, begin validate polling now
            maybeStartAccessValidationPolling(conversationId, socket);
          } else if (isPaused) {
            logger.info('Recording paused', {
              component: COMPONENT_NAME,
              action: 'STATUS_PAUSED',
              context: { conversationId, timestamp: Date.now() }
            });
            setStatus('Paused');
          } else {
            setStatus('Live');
          }


          // we divided the data[text, is_partial, div_id, sequence_id, is_corrected, start_time]
          const { text, is_partial, div_id, sequence_id, start_time } = data;
          if (!paragraphArrivalTimesRef.current[div_id]) {
            paragraphArrivalTimesRef.current[div_id] = Date.now();
            if (firstParagraphArrivalTimeRef.current === null) {
              firstParagraphArrivalTimeRef.current = Date.now();
            }
          }

          //if text is empty it will ignore
          if (!text || !text.trim()) {
            if (!isPaused) return;
          }

          setSegmentMap(prev => {
            const updated = { ...prev };

            // Initialize div_id array if it doesn't exist
            if (!updated[div_id]) {
              updated[div_id] = [];

              // Clean up boundary duplicates between paragraphs for short text
              const divOrderArr = divOrder.length ? divOrder : Object.keys(updated);
              const prevDivId = divOrderArr.length > 0 ? divOrderArr[divOrderArr.length - 1] : null;

              if (prevDivId && updated[prevDivId] && updated[prevDivId].length > 0) {
                const newTextTrimmed = text.trim().toLowerCase();
                // More conservative duplicate removal for short text
                updated[prevDivId] = updated[prevDivId].filter(seg => {
                  const segTextTrimmed = seg.text.trim().toLowerCase();
                  // Only remove if exact match or if one is clearly contained in the other
                  return !(segTextTrimmed === newTextTrimmed ||
                          (segTextTrimmed.length > 3 && newTextTrimmed.includes(segTextTrimmed)) ||
                          (newTextTrimmed.length > 3 && segTextTrimmed.includes(newTextTrimmed)));
                });
              }
            }

            // Handle corrected segments (replace existing final with same sequence_id)
            if (isCorrected === true) {
              const segs = updated[div_id];
              const idx = segs.findIndex(seg => seg.final && seg.sequence_id === sequence_id);
              if (idx !== -1) {
                // Replace existing final segment with corrected version
                segs[idx] = { 
                  ...segs[idx], 
                  text, 
                  corrected: true, 
                  sequence_id,
                  timestamp: new Date() 
                };
              } else {
                // If not found, append as final corrected segment
                segs.push({ 
                  text, 
                  final: true, 
                  sequence_id, 
                  corrected: true, 
                  timestamp: new Date() 
                });
              }
              updated[div_id] = [...segs];
              
              logger.info('Corrected segment received and applied', {
                component: COMPONENT_NAME,
                action: 'CORRECTED_SEGMENT_APPLIED',
                context: { div_id, sequence_id, textLength: text.length, timestamp: Date.now() }
              });
              
              return updated;
            }

            if (!is_partial) {
              // Handle final segments - more robust deduplication
              const trimmedText = text.trim();
              const existingFinal = updated[div_id].find(seg =>
                seg.final && seg.text.trim().toLowerCase() === trimmedText.toLowerCase()
              );

              if (!existingFinal && trimmedText) {
                const existingStartTime = updated[div_id].length > 0 ? updated[div_id][0].start_time : null;
                updated[div_id] = updated[div_id].filter(seg =>
                  seg.final || !seg.text.trim().toLowerCase().includes(trimmedText.toLowerCase())
                );
                updated[div_id].push({ text, final: true, sequence_id, corrected: false, start_time: existingStartTime ?? start_time ?? null });
              }
            } else {
              // Handle partial segments - be more selective about what we keep
              const trimmedPartial = text.trim();

              // Only add partial if it's substantial and meaningful
              if (trimmedPartial &&
                  trimmedPartial.length > 2 &&
                  /[a-zA-Z0-9]/.test(trimmedPartial) &&
                  !trimmedPartial.match(/^[.,;:!?\s]*$/)) {

                // Check if we already have final text in this div_id
                const hasFinalText = updated[div_id].some(seg => seg.final);

                const shouldAddPartial = trimmedPartial.length > 5 || 
                                        (hasFinalText && trimmedPartial.length > 3) ||
                                        !hasFinalText;

                if (shouldAddPartial) {
                  const existingStartTime = updated[div_id].length > 0 ? updated[div_id][0].start_time : null;
                  // Remove all previous partials for this div_id
                  updated[div_id] = updated[div_id].filter(seg => seg.final);
                  // Add the new partial
                  updated[div_id].push({ text, final: false, sequence_id, corrected: false, start_time: existingStartTime ?? start_time ?? null });
                }
              }
            }

            return updated;
          });

          setDivOrder(prev => {
            if (!prev.includes(div_id)) {
              return [...prev, div_id];
            }
            return prev;
          });
          // Sentence boundary enhancement: overwrite prev div if fields are present
          if (
            data.prev_div_id &&
            typeof data.prev_div_corrected === 'string' &&
            data.prev_div_corrected.trim()
          ) {
            setSegmentMap(prev => {
              if (!prev[data.prev_div_id]) return prev; // prev div not rendered yet, skip

              // Preserve start_time from existing segments so the timestamp doesn't change
              const existingSegments = prev[data.prev_div_id];
              const existingStartTime = existingSegments && existingSegments.length > 0
                ? existingSegments[0].start_time
                : null;

              return {
                ...prev,
                [data.prev_div_id]: [{
                  text: data.prev_div_corrected.trim(),
                  final: true,
                  corrected: true,
                  sequence_id: 0,
                  timestamp: new Date(),
                  start_time: existingStartTime
                }]
              };
            });
          }
        }

        // Handle audio chunks - Updated to handle both Float32 base64 and legacy formats
        if (data.action === 'audio_chunk') {

          // Validate that we have some audio data
          if (!data.audio_data && !data.audio) {
            return;
          }

          // Additional check: if data.audio exists but is empty (0 length), skip
          if (data.audio && data.audio.length === 0) {
            return;
          }

          // Additional check: if data.audio_data exists but is empty, skip
          if (data.audio_data && data.audio_data.length === 0) {
            return;
          }

          try {
            // Skip audio processing if audio is not enabled - use ref to avoid closure issue
            if (!audioEnabledRef.current || !audioContextRef.current) {
              return;
            }

            if (data.format === "pcm_f32le" && data.audio_data) {

              // Decode base64 Float32 data
              const audioBytes = Uint8Array.from(atob(data.audio_data), c => c.charCodeAt(0));
              const float32Array = new Float32Array(audioBytes.buffer);

              if (float32Array.length === 0) {
                return;
              }

              // Ensure AudioContext is running
              if (audioContextRef.current.state === 'suspended') {
                audioContextRef.current.resume();
              }

              // Create audio buffer with Float32 data
              const audioBuffer = audioContextRef.current.createBuffer(
                data.channels || 1,
                float32Array.length,
                data.sample_rate || 16000
              );

              // Copy Float32 data directly to audio buffer
              const channelData = audioBuffer.getChannelData(0);
              channelData.set(float32Array);

              // Queue playback
              const source = audioContextRef.current.createBufferSource();
              source.buffer = audioBuffer;
              source.connect(audioContextRef.current.destination);

              const startAt = Math.max(nextStartTimeRef.current, audioContextRef.current.currentTime);
              source.start(startAt);
              nextStartTimeRef.current = startAt + audioBuffer.duration;
            } else if (data.audio && Array.isArray(data.audio) && data.audio.length > 0) {
              // LEGACY FORMAT: Handle Int16 array

              const int16Array = new Int16Array(data.audio);

              if (int16Array.length === 0) {
                return;
              }

              // Ensure AudioContext is running
              if (audioContextRef.current.state === 'suspended') {
                audioContextRef.current.resume();
              }

              // Create audio buffer
              const audioBuffer = audioContextRef.current.createBuffer(1, int16Array.length, 16000);
              const float32 = audioBuffer.getChannelData(0);
              for (let i = 0; i < int16Array.length; i++) {
                float32[i] = int16Array[i] / 0x7FFF;
              }

              const source = audioContextRef.current.createBufferSource();
              source.buffer = audioBuffer;
              source.connect(audioContextRef.current.destination);

              const startAt = Math.max(nextStartTimeRef.current, audioContextRef.current.currentTime);
              source.start(startAt);
              nextStartTimeRef.current = startAt + audioBuffer.duration;

            } else {
            }

          } catch (audioError) {
          }
        }

        if (data.action === 'join_ok') {
          logger.info('Successfully joined session', {
            component: COMPONENT_NAME,
            action: 'SESSION_JOINED',
            userId,
            context: { conversationId, timestamp: Date.now() }
          });
        }

        if (data.action === 'configuration_viewer') {
          // Check for deleted conversation message FIRST
          if (data.code === ERROR_CODES.CONVERSATION_DELETED && data.latest_status === 'DELETED') {
            const deletedMessage = data.message || 'This conversation no longer exists';
            handleConversationDeleted(deletedMessage, socketRef);
            return;
          }

          setStatusFetched(true);
          
          if (typeof data.latest_status === 'string') {
            const statusMap = {
              'LIVE': 'Live',
              'PAUSED': 'Paused',
              'STOPPED': 'Stopped'
          
            };
            setStatus(statusMap[data.latest_status]);

            if (data.latest_status === 'STOPPED') {
              statusStoppedRef.current = true;
              // If the WebSocket already closed, begin validate polling now
              maybeStartAccessValidationPolling(conversationId, socket);
            }
            
            logger.info('Configuration received from server', {
              component: COMPONENT_NAME,
              action: 'CONFIGURATION_RECEIVED',
              userId,
              context: {
                conversationId,
                latestStatus: data.latest_status,
                historicalMessagesCount: data.data?.length || 0,
                timestamp: Date.now()
              }
            });
          }
          
          if (data.data && Array.isArray(data.data)) {
            const newSegmentMap = {};
            const newDivOrder = [];
            
            const now = Date.now();
            data.data.forEach((item) => {
              if (item.div_id && item.message && item.message.trim()) {
                const itemStartTime = typeof item.start_time === 'number' ? item.start_time : null;
                // Add to segment map with final status
                newSegmentMap[item.div_id] = [{
                  text: item.message.trim(),
                  final: true,
                  start_time: itemStartTime
                }];
                
                // Add to div order to maintain sequence
                newDivOrder.push(item.div_id);

                if (itemStartTime != null) {
                  lastHistoricalStartTimeRef.current = itemStartTime;
                  lastHistoricalArrivalRef.current = now;
                }

                // store div_id if message is an s3://... placeholder
                if ((item.message || "").includes("s3://")) {
                  s3DivIdsRef.current.add(item.div_id);

                }
              }
            });
            
            // Update state with historical data
            setSegmentMap(prev => ({ ...prev, ...newSegmentMap }));
            setDivOrder(prev => {
              // Merge with existing divOrder, avoiding duplicates
              const existingDivIds = new Set(prev);
              const newDivIds = newDivOrder.filter(id => !existingDivIds.has(id));
              return [...newDivIds, ...prev];
            });
            
          }
        }

        if (data.action === "presigned_urls_viewer" && Array.isArray(data.data)) {
  setSegmentMap(prev => {
    const updated = { ...prev };

    data.data.forEach(({ div_id, presigned_url }) => {
      if (!div_id || !presigned_url) return;

      const url = presigned_url.trim();

      // Preserve start_time from the existing segment (set by configuration_viewer)
      const existingSegments = updated[div_id];
      const existingStartTime = existingSegments && existingSegments.length > 0
        ? existingSegments[0].start_time
        : null;

      // simple image detection (png/jpg/jpeg/webp/gif or image content-type query)
      const isImage =
        /\.(png|jpe?g|webp|gif)(\?|$)/i.test(url) ||
        /image\//i.test(url) ||
        /\/images\//i.test(url);

      updated[div_id] = [{
        text: isImage
          ? `<img src="${url}" alt="attachment" class="transcript-image" />`
          : `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`,
        final: true,
        start_time: existingStartTime
      }];
    });

    return updated;
  });

  return;
}



      } catch (err) {
        logger.error('Error parsing WebSocket message', {
          component: COMPONENT_NAME,
          action: 'WEBSOCKET_MESSAGE_ERROR',
          error: err,
          context: { timestamp: Date.now() }
        });
      }
    };

    socket.onerror = (err) => {
      logger.error('WebSocket error', {
        component: COMPONENT_NAME,
        action: 'WEBSOCKET_ERROR',
        error: err,
        context: {
          conversationId,
          timestamp: Date.now()
        }
      });
      setStatus("Error connecting");
    };

    socket.onclose = () => {
      logger.warn('WebSocket closed', {
        component: COMPONENT_NAME,
        action: 'WEBSOCKET_CLOSED',
        userId,
        context: {
          conversationId,
          timestamp: Date.now()
        }
      });

      // Mark the viewer's WebSocket connection as stopped.
      // If the recording status is also 'Stopped', begin polling
      // GET /public_access/{conversationId}/validate every 5 seconds.
      wsClosedRef.current = true;
      maybeStartAccessValidationPolling(conversationId, socket);
    };

    // Cleanup on unmount
    return () => {
      logger.info('LiveTranscript component unmounting', {
        component: COMPONENT_NAME,
        action: 'COMPONENT_UNMOUNT',
        userId,
        context: {
          conversationId,
          segmentsCount: Object.keys(segmentMap).length,
          timestamp: Date.now()
        }
      });
      if (pingInterval) clearInterval(pingInterval);
      stopAccessValidationPolling();
      socket.close();
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    // Read profile setting for disabling audio
    const storedUser = JSON.parse(localStorage.getItem('user'));
    if (storedUser && storedUser.disableLiveTranscriptAudio) {
      setDisableAudioByProfile(true);
    } else {
      setDisableAudioByProfile(false);
    }
  }, []);

  const handleBackToHome = () => {
    logger.info('Navigating back to home', {
      component: COMPONENT_NAME,
      action: 'CLICK_BACK_TO_HOME',
      context: { timestamp: Date.now() }
    });
    navigate(routesfe.HOME);
  };


      // Auto-scroll using bottomRef + double requestAnimationFrame
    useEffect(() => {
    if (!autoScrollEnabled) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Always scroll to bottomRef for the most reliable scroll to latest content
        if (bottomRef.current) {
          bottomRef.current.scrollIntoView({
            behavior: "smooth",
            block: "end"
          });
        } else if (latestSegmentRef.current) {
          // Fallback to latestSegmentRef if bottomRef is not available
          latestSegmentRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center" // keeps the active line centered
          });
        }
      });
    });
  }, [segmentMap, autoScrollEnabled, statusFetched]);

  return (
    <div style={{
      fontFamily:
        viewerFont === 'system' ? 'Segoe UI, Arial, sans-serif'
        : viewerFont === 'sans' ? 'Arial, Helvetica, sans-serif'
        : viewerFont === 'serif' ? 'Georgia, Times, serif'
        : 'monospace',
      background: '#ffffff',
      height: '100vh',
      padding: isMobile ? '0 8px' : '0',
      margin: '0',
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0 // ✅ important
    }}>
      {/* Card Container */}
      <div style={{
        width: '100%',
        maxWidth: isMobile ? '100%' : isTablet ? '90%' : '1000px',
        margin: isMobile ? '10px auto' : '20px auto',
        background: 'rgba(255,255,255,0.98)',
        borderRadius: isMobile ? '12px' : '24px',
        boxShadow: '0 12px 32px rgba(26,115,232,0.08)',
        padding: isMobile ? '16px 12px' : isTablet ? '24px 20px' : '32px 28px 24px 28px',
        border: '1px solid #e3eafc',
        animation: 'fadeIn 0.7s ease',
        flex: '1',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap',
          marginBottom: isMobile ? '12px' : '18px'
        }}>
          <h2 style={{
            fontWeight: 700,
            fontSize: isMobile ? '1.4rem' : isTablet ? '1.8rem' : '2.1rem',
            color: '#14375A',
            margin: 0
          }}>
            <span style={{ marginRight: isMobile ? 6 : 10 }}>📝</span>Note Recording
          </h2>

          <div className="d-flex align-items-center" style={{ gap: '8px' }}>
            {/* Viewer Preferences Dropdown */}
            <div style={{ position: 'relative' }}>
              <button
                aria-label="Viewer preferences"
                style={{
                  width: '40px', height: '40px', borderRadius: '8px', border: 'none', background: '#f8f9fa', fontSize: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', marginRight: '4px'
                }}
                onClick={() => setShowPrefDropdown(v => !v)}
              >
                <span style={{ fontWeight: 600 }}>Aa</span>
              </button>
              {showPrefDropdown && (
                <div
                  className={`viewer-pref-dropdown${isMobile ? ' viewer-pref-dropdown-mobile' : isTablet ? ' viewer-pref-dropdown-tablet' : ''}`}
                  style={{
                    position: 'absolute',
                    top: '44px',
                    right: 0,
                    zIndex: 100,
                    minWidth: isMobile ? '180px' : isTablet ? '200px' : '220px',
                    maxWidth: isMobile ? '95vw' : isTablet ? '350px' : '350px',
                    background: '#fff',
                    border: '1px solid #e3eafc',
                    borderRadius: '10px',
                    boxShadow: '0 4px 16px rgba(26,115,232,0.09)',
                    padding: isMobile ? '8px 8px' : isTablet ? '10px 12px' : '12px 16px',
                    fontSize: isMobile ? '14px' : isTablet ? '15px' : '15px',
                    overflowY: 'auto',
                    maxHeight: isMobile ? '220px' : isTablet ? '260px' : '320px',
                  }}
                >
                  <div style={{ marginBottom: '8px', fontWeight: 600, fontSize: isMobile ? '13px' : '15px' }}>Text Size</div>
                  <div style={{ display: 'flex', gap: isMobile ? '4px' : '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                    {['small','medium','large','xlarge'].map(size => (
                      <button
                        key={size}
                        style={{
                          padding: isMobile ? '3px 7px' : '4px 10px',
                          borderRadius: '6px',
                          border: viewerTextSize === size ? '2px solid #1a73e8' : '1px solid #e3eafc',
                          background: viewerTextSize === size ? '#e3eafc' : '#f8f9fa',
                          fontWeight: 500,
                          fontSize: isMobile ? (size==='small'? '12px': size==='medium'? '13px': size==='large'? '15px':'16px') : (size==='small'? '13px': size==='medium'? '15px': size==='large'? '17px':'19px'),
                          cursor: 'pointer',
                          minWidth: isMobile ? '48px' : '60px',
                        }}
                        onClick={() => { setViewerTextSize(size); setShowPrefDropdown(false); }}
                      >
                        {size.charAt(0).toUpperCase()+size.slice(1)}
                      </button>
                    ))}
                  </div>
                  <div style={{ marginBottom: '8px', fontWeight: 600, fontSize: isMobile ? '13px' : '15px' }}>Font</div>
                  <div style={{ display: 'flex', gap: isMobile ? '4px' : '8px', flexWrap: 'wrap' }}>
                    {[
                      { key: 'system', label: 'System' },
                      { key: 'sans', label: 'Sans Serif' },
                      { key: 'serif', label: 'Serif' },
                      { key: 'mono', label: 'Monospace' }
                    ].map(font => (
                      <button
                        key={font.key}
                        style={{
                          padding: isMobile ? '3px 7px' : '4px 10px',
                          borderRadius: '6px',
                          border: viewerFont === font.key ? '2px solid #1a73e8' : '1px solid #e3eafc',
                          background: viewerFont === font.key ? '#e3eafc' : '#f8f9fa',
                          fontWeight: 500,
                          fontSize: isMobile ? '13px' : '15px',
                          cursor: 'pointer',
                          fontFamily: font.key==='system'? 'inherit': font.key==='sans'? 'Arial, Helvetica, sans-serif': font.key==='serif'? 'Georgia, Times, serif': 'monospace',
                          minWidth: isMobile ? '60px' : '80px',
                        }}
                        onClick={() => { setViewerFont(font.key); setShowPrefDropdown(false); }}
                      >
                        {font.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {/* Kebab Menu */}
            <Dropdown>
              <Dropdown.Toggle
                variant="outline-secondary"
                id="dropdown-basic"
                className="border-0 d-flex align-items-center justify-content-center no-caret-toggle"
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '8px',
                  backgroundColor: '#f8f9fa',
                  fontSize: '18px'
                }}
              >
                ⋮
              </Dropdown.Toggle>

              <Dropdown.Menu align="end" className="shadow-sm border-0" style={{ minWidth: '280px' }}>
              {/* Audio Toggle - only show if sendAudioChunk is not false */}
              {showLiveAudioOption && (
                <div 
                  className={`dropdown-item-toggle ${disableAudioByProfile ? 'disabled' : ''}`}
                >
                  <div className="d-flex flex-column">
                    <span style={{ fontWeight: '600', fontSize: '14px' }}>
                      {disableAudioByProfile ? 'Audio Disabled' : 'Live Audio'}
                    </span>
                    <small style={{ color: '#6c757d', fontSize: '12px' }}>
                      {disableAudioByProfile 
                        ? 'Disabled by profile settings' 
                        : audioEnabled 
                          ? 'Audio playback enabled' 
                          : 'Audio playback disabled'}
                    </small>
                  </div>
                  <label className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={audioEnabled}
                      disabled={disableAudioByProfile}
                      onChange={toggleAudio}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              )}

              <Dropdown.Divider />

              {/* Auto-scroll Toggle */}
              <div className="dropdown-item-toggle">
                <div className="d-flex flex-column">
                  <span style={{ fontWeight: '600', fontSize: '14px' }}>
                    Auto-scroll
                  </span>
                  <small style={{ color: '#6c757d', fontSize: '12px' }}>
                    {autoScrollEnabled 
                      ? 'Scrolling to latest text' 
                      : 'Manual scroll enabled'}
                  </small>
                </div>
                <label className="toggle-switch">
                  <input 
                    type="checkbox" 
                    checked={autoScrollEnabled}
                    onChange={toggleAutoScroll}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </Dropdown.Menu>

            </Dropdown>
          </div>
        </div>
        <div
          ref={outRef}
          style={{
            whiteSpace: 'pre-wrap',
            fontSize:
              viewerTextSize === 'small' ? (isMobile ? '0.8em' : isTablet ? '0.95em' : '1em')
              : viewerTextSize === 'medium' ? (isMobile ? '0.95em' : isTablet ? '1.08em' : '1.15em')
              : viewerTextSize === 'large' ? (isMobile ? '1.13em' : isTablet ? '1.22em' : '1.28em')
              : (isMobile ? '1.28em' : isTablet ? '1.38em' : '1.45em'),
            lineHeight: isMobile ? '1.4' : '1.5',
            overflowY: 'scroll',
            flex: 1, // Take all available vertical space
            minHeight: isMobile ? '180px' : isTablet ? '280px' : '400px',
            border: '1px solid #e3eafc',
            padding: isMobile ? '12px 12px 160px 12px': isTablet ? '16px 16px 180px 16px': '18px 18px 200px 18px',
            borderRadius: isMobile ? '8px' : '14px',
            backgroundColor: '#f8f9fa', 
            boxShadow: '0 2px 12px rgba(26,115,232,0.04)',
            marginBottom: '10px',
            color: '#2c3e50',
            wordWrap: 'break-word',
            fontFamily:
              viewerFont === 'system' ? 'Segoe UI, Arial, sans-serif'
              : viewerFont === 'sans' ? 'Arial, Helvetica, sans-serif'
              : viewerFont === 'serif' ? 'Georgia, Times, serif'
              : 'monospace'
          }}
          onClick={() => setShowPrefDropdown(false)}
        >
           {getParagraphs().map((para, idx, arr) => {
            const timeLabel = (para.start_time != null && !isNaN(para.start_time))
              ? `${Math.floor(para.start_time / 60)}:${String(Math.floor(para.start_time % 60)).padStart(2, '0')}`
              : null;
            return (
            <div
              key={para.div_id}
              className="transcript-paragraph-block"
              style={{ marginBottom: '0.5em', paddingBottom: '0.5em', fontFamily:
                viewerFont === 'system' ? 'Segoe UI, Arial, sans-serif'
                : viewerFont === 'sans' ? 'Arial, Helvetica, sans-serif'
                : viewerFont === 'serif' ? 'Georgia, Times, serif'
                : 'monospace',
                fontSize:
                  viewerTextSize === 'small' ? 'inherit'
                  : viewerTextSize === 'medium' ? 'inherit'
                  : viewerTextSize === 'large' ? '1.15em'
                  : '1.22em'
              }}
              ref={idx === arr.length - 1 ? latestSegmentRef : null}
            >
              <div className="transcript-speaker-header">
                <div className="transcript-speaker-avatar">
                  <BsPersonFill />
                </div>
                {timeLabel && (
                  <span className="transcript-paragraph-timestamp">{timeLabel}</span>
                )}
              </div>
              <div className="transcript-segment-text">
                <span
                  className="segment-text"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(/^<[^>]+>/.test(para.text) ? para.text : wrapWordsWithSpans(para.text), {
                      ALLOWED_TAGS: [
                        'span', 'p', 'br', 'strong', 'em', 'u', 's', 'h1', 'h2', 'h3',  
                        'ol', 'ul', 'li', 'blockquote', 'code', 'pre', 'a', 'img'
                      ],
                      ALLOWED_ATTR: ['href','src','alt','title','target','rel','class','style']
                    })
                  }}
                />
              </div>
            </div>
          );
          })}
          {/* Bottom sentinel used as reliable target for autoscroll - with visual gap for 2-3 lines */}
          <div ref={bottomRef} style={{ 
            width: '100%', 
            height: isMobile ? '60px' : isTablet ? '80px' : '100px',
            opacity: 0, 
            pointerEvents: 'none',
            marginTop: '0.5em'
          }}></div>
        </div>
        
      </div>

      {/* Status bar */} 
      <div className="live-footer" style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: isMobile ? '10px 8px' : '12px 0'
      }}>

        <div style={{
          fontWeight: 600,
          fontSize: isMobile ? '0.95em' : isTablet ? '1.05em' : '1.15em',
          color:
            status === 'Paused' ? '#ffc107'
            : status === 'Stopped' ? '#dc3545'
            : status === 'Live' ? '#28a745'
            : status === 'Access Revoked' ? '#dc3545'
            : '#666',
          background:
            status === 'Paused' ? 'transparent'
            : status === 'Stopped' ? 'transparent'
            : status === 'Live' ? 'rgba(40,167,69,0.13)'
            : status === 'Access Revoked' ? 'rgba(220,53,69,0.10)'
            : 'rgba(102,102,102,0.07)',
          borderRadius: isMobile ? '8px' : '14px',
          padding: isMobile ? '8px 12px' : isTablet ? '8px 18px' : '8px 22px',
          display: 'inline-block',
          boxShadow: '0 2px 8px rgba(26,115,232,0.07)',
          textAlign: 'center',
          minWidth: isMobile ? '120px' : 'auto'
        }}>
          {/* Show only Connecting… until status is fetched from table */}
          {!statusFetched ? 'Connecting…' : (
            <>
              {status === 'Paused' && '⏸️ '}
              {status === 'Stopped' && '⛔ '}
              {status === 'Live' && '🔴 '}
              {status === 'Access Revoked' && '🚫 '}
              {status}
            </>
          )}
        </div>
      </div>

      {/* Public access revoked → the owner-shared link is unavailable.
          The viewer is redirected via the existing deleted-conversation
          flow (see handlePublicAccessRevoked), so no empty conversation
          page and no saved transcript are rendered here. */}


    </div>
  );
};

export default LiveTranscript;
